import axios from "axios";

export type BasketOrderRecord = {
  recordId: string;
  recordType: "BOOKING" | "PRODUCT_PURCHASE" | "GIFT_CARD" | "BUNDLE_PURCHASE";
};

export type PollOptions = {
  timeoutMs?: number;
  intervalMs?: number;
};

const DEFAULT_API_URL = "https://api.baluu.io";

function normalizeBaseUrl(baseUrl: string): string {
  return (baseUrl || DEFAULT_API_URL).replace(/\/+$/, "");
}

/**
 * Polls /app/basketOrderStatus/:basketOrderId until the first linked record
 * has a recordId populated (Inngest has finished creating the downstream
 * Booking / ProductPurchase / BundlePurchase) or the timeout elapses.
 * Free baskets are always single-item, so we read linkedRecords[0].
 *
 * Transient HTTP errors on individual polls are swallowed so a single 502
 * doesn't abort the whole retry window — the loop keeps trying until the
 * deadline. A linked record with status "FAILED" is treated as a hard
 * failure and surfaced immediately.
 */
export async function pollBasketOrderRecord(
  basketOrderId: string,
  baseUrl: string,
  options?: PollOptions,
): Promise<BasketOrderRecord> {
  const timeoutMs = options?.timeoutMs ?? 15_000;
  const intervalMs = options?.intervalMs ?? 500;
  const deadline = Date.now() + timeoutMs;
  const url = `${normalizeBaseUrl(baseUrl)}/app/basketOrderStatus/${basketOrderId}`;

  // Per-request timeout. The deadline is only checked at the top of the loop,
  // so without this a single hung GET (no axios default timeout) would block
  // forever and never let the deadline fire. A timed-out request rejects with
  // ECONNABORTED (no `response.status`), so it's treated as a soft failure and
  // the loop simply retries until the overall deadline.
  const perRequestTimeoutMs = 5_000;

  while (Date.now() < deadline) {
    try {
      const response = await axios.get(url, { timeout: perRequestTimeoutMs });
      const linked = response.data?.linkedRecords?.[0];
      if (linked?.recordId && linked?.recordType) {
        return { recordId: linked.recordId, recordType: linked.recordType };
      }
      if (linked?.processingStatus === "FAILED") {
        throw new Error(
          `Basket order ${basketOrderId} item processing failed: ${linked.processingError ?? "unknown"}`,
        );
      }
    } catch (err: any) {
      // Surface hard failures immediately: a FAILED linkedRecord (thrown
      // above) or any 4xx HTTP response (the basket order doesn't exist,
      // we're unauthorized, the URL is wrong — none of which 15s of
      // retrying will fix). Swallow 5xx and network errors so a transient
      // blip doesn't abort the window — Inngest commonly takes 1–5s.
      if (err?.message?.startsWith("Basket order")) {
        throw err;
      }
      const status = err?.response?.status;
      if (typeof status === "number" && status >= 400 && status < 500) {
        throw err;
      }
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }

  throw new Error(
    `Timed out waiting for basket order ${basketOrderId} record to be created`,
  );
}

/**
 * Builds the type-specific post-success URL for a created basket-order record.
 * Mirrors the redirect targets `processChargeFromPaymentIntent.js` returns for
 * widget-source purchases. Unknown recordType values fall through to the
 * generic order page rather than producing an undefined URL.
 */
export function buildPostSuccessUrl(
  record: BasketOrderRecord,
  returnOrigin: string,
  options?: { checkoutMode?: string },
): string {
  const isPageMode = options?.checkoutMode === "page";
  const suffix = isPageMode ? "?checkout-mode=page" : "";

  switch (record.recordType) {
    case "BOOKING":
      return `${returnOrigin}/classroom/${record.recordId}${suffix}`;
    case "PRODUCT_PURCHASE":
      return `${returnOrigin}/payment/product/success/${record.recordId}`;
    case "BUNDLE_PURCHASE":
      return `${returnOrigin}/bundles/manage/${record.recordId}`;
    case "GIFT_CARD":
      return `${returnOrigin}/sucessfullOrder/${record.recordId}`;
    default:
      return `${returnOrigin}/sucessfullOrder/${record.recordId}`;
  }
}
